// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { getEffectiveLocationName } from '@/location/utilities/get-effective-third-party';
import { SparseLocationWithParent } from '@/sparse/api/types';
import { pluralize } from '@/utilities/pluralize';
import { Stack, Tooltip, Typography } from '@mui/material';

interface Props {
  /**
   * A list of location associated with a project.
   */
  locations: Pick<
    SparseLocationWithParent,
    'id' | 'name' | 'type' | 'parentLocation' | 'zoneCount'
  >[];

  /**
   * If provided, only the locations up to this count will be displayed. A
   * list of locations with a length greater than this value will be pluralized
   * instead.
   */
  maximumLocationCount?: number;

  /**
   * If true, the project is archived and the label will use the disabled color.
   */
  isArchived?: boolean;
}

/**
 * A label component that renders a project's locations or an empty state if
 * there are no locations.
 */
export function ProjectLocationsLabel(props: Props) {
  if (props.locations.length === 0) {
    const color = props.isArchived ? 'text.disabled' : 'text.secondary';

    return (
      <Typography variant="body2" color={color} marginTop={0.5}>
        Not set
      </Typography>
    );
  }

  const color = props.isArchived ? 'text.disabled' : 'text.primary';
  const maximumLocationCount =
    props.maximumLocationCount ?? props.locations.length;

  if (props.locations.length > maximumLocationCount) {
    const tooltip = (
      <ul style={{ margin: 0, padding: 0, listStyleType: 'none' }}>
        {props.locations.map((location) => (
          <li key={location.id}>{getEffectiveLocationName(location)}</li>
        ))}
      </ul>
    );

    return (
      <Tooltip arrow placement="right" title={tooltip}>
        <Typography
          color={color}
          variant="body2"
          display="inline"
          textAlign="left"
          marginTop={0.5}
        >
          {pluralize(props.locations.length, { singular: 'location' })}
        </Typography>
      </Tooltip>
    );
  }

  return (
    <Stack marginTop={0.5}>
      {props.locations.map((location) => (
        <Typography
          key={location.id}
          variant="body2"
          textAlign="left"
          color={color}
        >
          {getEffectiveLocationName(location)}
        </Typography>
      ))}
    </Stack>
  );
}
